// Where the browser keeps an administrator's session token.
//
// sessionStorage rather than localStorage: it is scoped to the tab and cleared
// when the tab closes, so a shared machine does not leave an administrative
// session behind for the next person. The cost is signing in again per tab,
// which is the right trade for a credential that can change storage
// credentials and the timestamp authority.
//
// Not a cookie, because the API is a different origin (api.signrs.com) and
// authenticates with a bearer token (spec/04 §8a).

const KEY = 'esign.session';

export interface StoredSession {
  readonly token: string;
  readonly email: string;
  readonly organisationName: string;
  readonly expiresAt: string;
}

export function readSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    // An expired token is worse than none: it produces a 401 from somewhere
    // deep in a screen rather than a sign-in form.
    if (Date.parse(parsed.expiresAt) <= Date.now()) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function storeSession(session: StoredSession): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    /* A browser refusing storage is a browser that signs in again. */
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}
